import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import axios from 'axios';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-material-ui';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import Container from '@material-ui/core/Container';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Snackbar from '@material-ui/core/Snackbar';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Toolbar from '@material-ui/core/Toolbar';
import AccountCircle from '@material-ui/icons/AccountCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import { ReactComponent as Logo } from './logo-white.svg';

const goToCustomerPortal = async (user) => {
  const { data } = await axios.get(
    `${process.env.REACT_APP_API_URL}/user/customerPortal`,
    {
      headers: { Authorization: `Bearer ${user.token}` },
    },
  );

  if (data.url) {
    document.location = data.url;
  }
};

const removeUser = async (user, id) =>
  axios.delete(`${process.env.REACT_APP_API_URL}/user/${id}`, {
    headers: { Authorization: `Bearer ${user.token}` },
  });

const updateStatus = async (user, id, status) =>
  axios.put(
    `${process.env.REACT_APP_API_URL}/user/${id}`,
    { status },
    {
      headers: { Authorization: `Bearer ${user.token}` },
    },
  );

const toggleLicense = async (license, user, token) =>
  axios.put(
    `${process.env.REACT_APP_API_URL}/user/license`,
    {
      id: license,
      user,
    },
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

const useStyles = makeStyles((theme) => ({
  spacer: {
    flexGrow: 1,
  },
}));

const Home = ({ user, setUser }) => {
  const classes = useStyles();
  const [team, setTeam] = useState([]);
  const updateTeam = async () => {
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/team`,
      {
        headers: { Authorization: `Bearer ${user.token}` },
      },
    );
    setTeam(data);
  };

  const logout = () => {
    sessionStorage.clear();
    setUser({});
  };

  const updateUser = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/user/me`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
        },
      );
      setUser(data);
    } catch (err) {
      logout();
    }
  };

  useEffect(() => {
    if (user.token) {
      updateUser();
      updateTeam();
    }
  }, []);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [licenseDialogOpen, setLicenseDialogOpen] = React.useState(false);

  const handleDialogOpen = (x) => {
    setDialogOpen(x);
  };

  const handleLicenseDialogOpen = (x) => {
    setLicenseDialogOpen(x);
  };

  const handleDialogClose = (remove) => {
    if (remove) {
      removeUser(user, dialogOpen.id).then(updateTeam);
    }
    setDialogOpen(false);
  };

  const handleLicenseDialogClose = () => {
    setLicenseDialogOpen(false);
  };

  const [snackOpen, setSnackOpen] = React.useState(false);
  const licenses = {
    AA: user.company.licenses.find((x) => x.provider === 'AA'),
    jira: user.company.licenses.find((x) => x.provider === 'jira'),
    facebook: user.company.licenses.find((x) => x.provider === 'facebook'),
    google: user.company.licenses.find((x) => x.provider === 'google'),
  };

  const licenseCheck = (provider, member) => {
    if (
      !licenses[provider] ||
      (!licenses[provider].users.includes(member.id) &&
        licenses[provider].users.length >= licenses[provider].seats)
    ) {
      return handleLicenseDialogOpen(provider);
    }

    toggleLicense(licenses[provider].id, member.id, user.token).then(
      updateUser,
    );
  };

  return (
    <>
      <AppBar elevation={0}>
        <Toolbar>
          <a href="https://www.daexus.io">
            <Logo />
          </a>
          <span className={classes.spacer} />
          {user.email && (
            <>
              <IconButton onClick={handleClick} color="inherit">
                <AccountCircle />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'left',
                }}
                getContentAnchorEl={null}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={handleClose}
              >
                <MenuItem disabled>{user.email}</MenuItem>
                <MenuItem
                  disabled={user.status !== 'admin'}
                  onClick={() => goToCustomerPortal(user)}
                >
                  Billing
                </MenuItem>
                <MenuItem onClick={logout}>Log out</MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Box p={6} />
      <Container component={Paper} variant="outlined" maxWidth="md">
        <Box my={2}>
          <Formik
            initialValues={{ name: user.company.name }}
            enableReinitialize
          >
            {({ isSubmitting, setSubmitting, values }) => (
              <Form>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs>
                    <Field
                      type="text"
                      name="name"
                      label="Company Name"
                      component={TextField}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      disabled={user.status !== 'admin'}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  {user.status === 'admin' && (
                    <Grid item>
                      <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        disabled={
                          isSubmitting ||
                          !values.name ||
                          values.name === user.company.name
                        }
                        onClick={async () => {
                          setSubmitting(true);
                          try {
                            await axios.put(
                              `${process.env.REACT_APP_API_URL}/user/company`,
                              values,
                              {
                                headers: {
                                  Authorization: `Bearer ${user.token}`,
                                },
                              },
                            );
                            await updateUser();
                          } catch (err) {}
                          setSubmitting(false);
                        }}
                      >
                        Rename
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Form>
            )}
          </Formik>
          {user.status === 'admin' && (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ verticalAlign: 'bottom' }}>
                      Email
                    </TableCell>
                    <TableCell
                      style={{ verticalAlign: 'bottom', width: 75 }}
                      align="center"
                    >
                      Admin
                    </TableCell>
                    <TableCell
                      style={{ verticalAlign: 'bottom', width: 75 }}
                      align="center"
                    >
                      <div style={{ marginBottom: -20 }}>
                        <div style={{ lineHeight: 'normal' }}>
                          Adobe Analytics
                        </div>
                        <a
                          href="#"
                          onClick={() => handleLicenseDialogOpen('AA')}
                          style={{ textDecoration: 'underline' }}
                        >
                          {licenses.AA
                            ? `${licenses.AA.users.length}/${licenses.AA.seats}`
                            : 'Upgrade'}
                        </a>
                      </div>
                    </TableCell>
                    <TableCell
                      style={{ verticalAlign: 'bottom', width: 75 }}
                      align="center"
                    >
                      <div style={{ marginBottom: -20 }}>
                        <div style={{ lineHeight: 'normal' }}>Jira</div>
                        <a
                          href="#"
                          onClick={() => handleLicenseDialogOpen('jira')}
                          style={{ textDecoration: 'underline' }}
                        >
                          {licenses.jira
                            ? `${licenses.jira.users.length}/${licenses.jira.seats}`
                            : 'Upgrade'}
                        </a>
                      </div>
                    </TableCell>
                    <TableCell
                      style={{ verticalAlign: 'bottom', width: 75 }}
                      align="center"
                    >
                      <div style={{ marginBottom: -20 }}>
                        <div style={{ lineHeight: 'normal' }}>Facebook Ads</div>
                        <a
                          href="#"
                          onClick={() => handleLicenseDialogOpen('facebook')}
                          style={{ textDecoration: 'underline' }}
                        >
                          {licenses.facebook
                            ? `${licenses.facebook.users.length}/${licenses.facebook.seats}`
                            : 'Upgrade'}
                        </a>
                      </div>
                    </TableCell>
                    <TableCell
                      style={{ verticalAlign: 'bottom', width: 75 }}
                      align="center"
                    >
                      <div style={{ marginBottom: -20 }}>
                        <div style={{ lineHeight: 'normal' }}>
                          Google Analytics
                        </div>
                        <a
                          href="#"
                          onClick={() => handleLicenseDialogOpen('google')}
                          style={{ textDecoration: 'underline' }}
                        >
                          {licenses.google
                            ? `${licenses.google.users.length}/${licenses.google.seats}`
                            : 'Upgrade'}
                        </a>
                      </div>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {team.map((x) => (
                    <TableRow key={x.email}>
                      <TableCell>
                        {x.email}
                        {x.status === 'invitee' && (
                          <small style={{ display: 'block' }}>
                            invitation pending,{' '}
                            <a
                              href="#"
                              color="primary"
                              onClick={() =>
                                axios
                                  .post(
                                    `${process.env.REACT_APP_API_URL}/user/invite`,
                                    {
                                      email: x.email,
                                      resend: true,
                                    },
                                    {
                                      headers: {
                                        Authorization: `Bearer ${user.token}`,
                                      },
                                    },
                                  )
                                  .then(() => setSnackOpen(true))
                              }
                              style={{
                                textTransform: 'none',
                                textDecoration: 'underline',
                              }}
                            >
                              resend
                            </a>
                          </small>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={x.status === 'admin'}
                          disabled={x.id === user.id || x.status === 'invitee'}
                          onChange={() =>
                            updateStatus(
                              user,
                              x.id,
                              x.status === 'user' ? 'admin' : 'user',
                            ).then(updateTeam)
                          }
                        />
                      </TableCell>
                      {['AA', 'jira', 'facebook', 'google'].map((provider) => (
                        <TableCell key={provider} align="center">
                          <Checkbox
                            checked={Boolean(
                              licenses[provider] &&
                                licenses[provider].users.includes(x.id),
                            )}
                            disabled={x.status === 'invitee'}
                            onChange={() => licenseCheck(provider, x)}
                          />
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        {x.id !== user.id && (
                          <IconButton
                            onClick={() => handleDialogOpen(x)}
                            style={{ color: 'red' }}
                            size="small"
                            edge="end"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <Formik
                    initialValues={{ email: '', link: '' }}
                    onSubmit={async (
                      values,
                      {
                        setFieldError,
                        setFieldTouched,
                        setFieldValue,
                        setSubmitting,
                      },
                    ) => {
                      setSubmitting(true);
                      try {
                        const { data } = await axios.post(
                          `${process.env.REACT_APP_API_URL}/user/invite`,
                          values,
                          {
                            headers: {
                              Authorization: `Bearer ${user.token}`,
                            },
                          },
                        );
                        setSnackOpen(true);
                        setFieldValue('link', data.message);
                        setFieldValue('email', '');
                        updateTeam();
                      } catch (err) {
                        setFieldTouched('email', true, false);
                        setFieldError('email', err.response.data.error);
                      }
                      setSubmitting(false);
                    }}
                  >
                    {({ errors, isSubmitting, values }) => (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Field
                            type="email"
                            name="email"
                            label="Invite another colleague"
                            placeholder="example@daexus.io"
                            component={TextField}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            error={Boolean(errors.email)}
                            helperText={errors.email}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </TableCell>
                        <TableCell colSpan={2}>
                          <Form>
                            <Button
                              type="submit"
                              color="secondary"
                              disabled={isSubmitting || !values.email}
                              style={{
                                textTransform: 'none',
                              }}
                            >
                              Send invite
                            </Button>
                          </Form>
                        </TableCell>
                      </TableRow>
                    )}
                  </Formik>
                </TableBody>
              </Table>
              <Dialog
                open={Boolean(dialogOpen)}
                onClose={handleDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogTitle id="alert-dialog-title">
                  {'Remove user?'}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="alert-dialog-description">
                    Are you sure you want to remove the user {dialogOpen.email}?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => handleDialogClose(false)}
                    color="primary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleDialogClose(true)}
                    color="primary"
                    style={{ color: 'red' }}
                  >
                    Remove
                  </Button>
                </DialogActions>
              </Dialog>
              <Dialog
                open={Boolean(licenseDialogOpen)}
                onClose={handleLicenseDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
              >
                <DialogTitle id="alert-dialog-title">Licensing</DialogTitle>
                <DialogContent>
                  <DialogContentText id="alert-dialog-description">
                    {licenses[licenseDialogOpen] ? (
                      <p>
                        <p>
                          You are currently on the{' '}
                          <b>
                            {licenses[licenseDialogOpen].plan.toUpperCase()}
                          </b>{' '}
                          plan.
                        </p>
                        <p>
                          You have{' '}
                          <b>{licenses[licenseDialogOpen].users.length}</b>{' '}
                          seats assigned out of a total of{' '}
                          <b>{licenses[licenseDialogOpen].seats}</b>.
                        </p>
                        {licenses[licenseDialogOpen].users.length >=
                          licenses[licenseDialogOpen].seats && (
                          <b style={{ color: 'red' }}>
                            You must buy more seats to assign colleagues!
                          </b>
                        )}
                        <p>
                          To upgrade your current plan or add more seats, reach
                          out to{' '}
                          <a href="mailto:support@daexus.io">
                            support@daexus.io
                          </a>
                        </p>
                      </p>
                    ) : (
                      <>
                        <p>
                          You are currently on the <b>free</b> plan.
                          <br />
                          Buy premium seats to enjoy extra functionalities:
                        </p>
                        <Button
                          variant="contained"
                          color="primary"
                          href={`https://buy.stripe.com/${
                            process.env[
                              'REACT_APP_STRIPE_' +
                                String(licenseDialogOpen).toUpperCase() +
                                '_PRO'
                            ]
                          }?client_reference_id=${
                            user.id
                          }&prefilled_email=${encodeURIComponent(user.email)}`}
                        >
                          Upgrade to PRO
                        </Button>{' '}
                        <Button
                          variant="contained"
                          color="secondary"
                          href={`https://buy.stripe.com/${
                            process.env[
                              'REACT_APP_STRIPE_' +
                                String(licenseDialogOpen).toUpperCase() +
                                '_SERVER'
                            ]
                          }?client_reference_id=${
                            user.id
                          }&prefilled_email=${encodeURIComponent(user.email)}`}
                        >
                          Upgrade to SERVER
                        </Button>
                      </>
                    )}
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => handleLicenseDialogClose(true)}
                    color="primary"
                  >
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
              <Snackbar
                open={snackOpen}
                autoHideDuration={10000}
                onClose={() => setSnackOpen(false)}
                message="Invitation sent!"
              />
            </>
          )}
        </Box>
      </Container>
      <Box my={4} />
    </>
  );
};

export default Home;
